import { PWA_UPDATE_STATUS } from "@/services/pwa-update-status";
import { recordError } from "@/services/record-error";
import { useStore } from "@/services/store";

import { registerSW } from "virtual:pwa-register";

const PWA_UPDATE_INTERVAL = 60 * 60 * 1000;
const PWA_UPDATE_DEDUPLICATION_DELAY = 60 * 1000;
const PWA_ACTIVATION_TIMEOUT = 30 * 1000;
const PWA_RELOAD_FALLBACK_DELAY = 1000;
const PWA_FRESH_START_KEY = "volvamed.pwa-fresh-start";
const PWA_FRESH_START_MAX_AGE = 60 * 1000;

let updateServiceWorker;
let serviceWorkerRegistration;
let lastUpdateCheck = null;
let activationTimeout;
let updateInterval;
let cleanupPwaListeners = () => {};

function isExpectedPwaError(error, operation) {
  const message = String(error?.message || error);

  if (
    operation === "registerPwa" &&
    error?.name === "SecurityError" &&
    message.includes("Failed to register a ServiceWorker") &&
    message.includes("workers are not available in this environment")
  )
    return true;
  if (
    operation === "registerPwa" &&
    (message === "Rejected" ||
      message.includes("Importing a module script failed") ||
      message.includes("Failed to fetch dynamically imported module") ||
      message.includes("sw.js load failed"))
  )
    return true;
  if (
    operation === "checkForPwaUpdate" &&
    message.includes("Failed to update a ServiceWorker") &&
    message.includes("unknown error occurred when fetching the script")
  )
    return true;
  if (
    operation === "checkForPwaUpdate" &&
    error?.name === "InvalidStateError" &&
    message.includes("newestWorker is null")
  )
    return true;

  return false;
}

function recordPwaError(error, operation) {
  recordError(error, {
    expected: isExpectedPwaError(error, operation),
    source: "services/pwa",
    operation,
  });
}

// The update reloads the page: the next boot reads this mark to play the
// "fresh app" animation once. sessionStorage can be unavailable (private
// mode, blocked storage), in which case the animation is simply skipped.
function rememberPwaFreshStart() {
  try {
    window.sessionStorage.setItem(PWA_FRESH_START_KEY, String(Date.now()));
  } catch {
    return;
  }
}

function forgetPwaFreshStart() {
  try {
    window.sessionStorage.removeItem(PWA_FRESH_START_KEY);
  } catch {
    return;
  }
}

function readPwaFreshStart() {
  try {
    const updatedAt = window.sessionStorage.getItem(PWA_FRESH_START_KEY);
    window.sessionStorage.removeItem(PWA_FRESH_START_KEY);
    if (updatedAt === null) return false;
    return Date.now() - Number(updatedAt) < PWA_FRESH_START_MAX_AGE;
  } catch {
    return false;
  }
}

function setReloadRequiredIfPageRemains() {
  window.setTimeout(() => {
    useStore.setState({
      pwaUpdateStatus: PWA_UPDATE_STATUS.RELOAD_REQUIRED,
    });
  }, PWA_RELOAD_FALLBACK_DELAY);
}

function reloadForPwaUpdate() {
  window.clearTimeout(activationTimeout);
  useStore.setState({ pwaUpdateStatus: PWA_UPDATE_STATUS.APPLYING });
  rememberPwaFreshStart();
  setReloadRequiredIfPageRemains();

  try {
    window.location.reload();
  } catch (error) {
    recordPwaError(error, "reloadForPwaUpdate");
    forgetPwaFreshStart();
    useStore.setState({
      pwaUpdateStatus: PWA_UPDATE_STATUS.RELOAD_REQUIRED,
    });
  }
}

async function checkForPwaUpdate() {
  if (!serviceWorkerRegistration) return;
  if (navigator.onLine === false) return;
  if (document.visibilityState === "hidden") return;
  if (serviceWorkerRegistration.installing) return;
  if (
    lastUpdateCheck !== null &&
    Date.now() - lastUpdateCheck < PWA_UPDATE_DEDUPLICATION_DELAY
  )
    return;

  lastUpdateCheck = Date.now();

  try {
    await serviceWorkerRegistration.update();
  } catch (error) {
    recordPwaError(error, "checkForPwaUpdate");
  }
}

export function registerPwa() {
  if (typeof window === "undefined") return;

  useStore.setState({ pwaFreshStart: readPwaFreshStart() });

  if (import.meta.env.DEV) return;
  if (!("serviceWorker" in navigator)) return;

  cleanupPwaListeners();
  window.clearInterval(updateInterval);

  const handleVisibilityChange = () => {
    if (document.visibilityState !== "visible") return;
    checkForPwaUpdate();
  };
  const handleOnline = () => {
    useStore.setState({ pwaOnline: true });
    checkForPwaUpdate();
  };
  const handleOffline = () => {
    useStore.setState({ pwaOnline: false });
  };

  useStore.setState({
    pwaOnline: navigator.onLine !== false,
    pwaUpdateStatus: PWA_UPDATE_STATUS.CURRENT,
  });
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  updateInterval = window.setInterval(checkForPwaUpdate, PWA_UPDATE_INTERVAL);
  cleanupPwaListeners = () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };

  updateServiceWorker = registerSW({
    immediate: true,
    onNeedRefresh() {
      useStore.setState({ pwaUpdateStatus: PWA_UPDATE_STATUS.AVAILABLE });
    },
    onNeedReload() {
      reloadForPwaUpdate();
    },
    onRegisteredSW(swScriptUrl, registration) {
      serviceWorkerRegistration = registration;
      lastUpdateCheck = null;
      checkForPwaUpdate();
    },
    onRegisterError(error) {
      recordPwaError(error, "registerPwa");
    },
  });
}

export async function applyPwaUpdate() {
  if (useStore.getState().pwaUpdateStatus !== PWA_UPDATE_STATUS.AVAILABLE)
    return;
  if (navigator.onLine === false) return;
  if (!updateServiceWorker) return;

  useStore.setState({ pwaUpdateStatus: PWA_UPDATE_STATUS.APPLYING });
  rememberPwaFreshStart();

  try {
    await updateServiceWorker();
  } catch (error) {
    recordPwaError(error, "applyPwaUpdate");
    forgetPwaFreshStart();
    useStore.setState({ pwaUpdateStatus: PWA_UPDATE_STATUS.AVAILABLE });
    return;
  }

  window.clearTimeout(activationTimeout);
  activationTimeout = window.setTimeout(() => {
    if (useStore.getState().pwaUpdateStatus !== PWA_UPDATE_STATUS.APPLYING)
      return;
    recordPwaError(
      new Error("PWA service worker activation timed out"),
      "applyPwaUpdate"
    );
    forgetPwaFreshStart();
    useStore.setState({ pwaUpdateStatus: PWA_UPDATE_STATUS.AVAILABLE });
  }, PWA_ACTIVATION_TIMEOUT);
}

export function reloadPwa() {
  if (useStore.getState().pwaUpdateStatus !== PWA_UPDATE_STATUS.RELOAD_REQUIRED)
    return;
  reloadForPwaUpdate();
}
